import type {
  AtlasSuggestionsContext,
  AtlasSuggestionsResponse,
} from "@intelligems/ig-types/functions/agentic-logic/atlas-suggestions/atlas-suggestions";
import { aiApiClient } from "@src/api/ai-api";
import { useQuery } from "@tanstack/react-query";

/**
 * The server generates a context's chips once and caches them for 30 minutes;
 * the client holds them as long, so moving between tabs and back never asks
 * again.
 */
const ATLAS_SUGGESTIONS_STALE_TIME = 30 * 60 * 1000;

export const atlasSuggestionsKey = (context: AtlasSuggestionsContext) =>
  ["atlas-suggestions", context] as const;

/**
 * The Atlas rail's suggestion chips for the screen it sits beside. `null`
 * asks for nothing (a screen with no chips). A failure is not retried: the
 * chips are an optional affordance and the rail works without them.
 */
export const useAtlasSuggestions = (context: AtlasSuggestionsContext | null) =>
  useQuery({
    queryKey: context
      ? atlasSuggestionsKey(context)
      : (["atlas-suggestions", null] as const),
    queryFn: async () => {
      if (!context) {
        throw new Error("A suggestions context is required");
      }
      const response = await aiApiClient.post<AtlasSuggestionsResponse>(
        "/atlas-suggestions",
        context
      );
      return response.data;
    },
    enabled: context !== null,
    staleTime: ATLAS_SUGGESTIONS_STALE_TIME,
    retry: false,
  });
